import { useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Send } from "lucide-react";
import { usePortalLanguage, tLang } from "@/contexts/PortalLanguageContext";
import { useLocation } from "react-router-dom";
import { useTurnstile } from "@/lib/turnstile";

const NAVY  = "#1A1A2E";
const EMBER = "#FF6B35";

const SB_URL      = import.meta.env.VITE_SUPABASE_URL  as string;
const SB_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export default function Contact() {
  const { t } = usePortalLanguage();
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";

  const [name,    setName]    = useState("");
  const [email,   setEmail]   = useState("");
  const [message, setMessage] = useState("");
  // Bot traps for contact-submit (see supabase/functions/_shared/formGuard.ts): `_hp` is a hidden field
  // real users never fill; elapsed_ms is the time since this form opened.
  const [hp, setHp] = useState("");
  const openedAt = useRef(Date.now());
  const turnstile = useTurnstile();
  const [status,  setStatus]  = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error,   setError]   = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim() || !email.trim() || !message.trim()) {
      setError(t("contact.form.errorRequired", "Please fill in all fields."));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t("contact.form.errorEmail", "Please enter a valid email address."));
      return;
    }

    setStatus("sending");

    try {
      const res = await fetch(`${SB_URL}/functions/v1/contact-submit?apikey=${encodeURIComponent(SB_ANON_KEY)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SB_ANON_KEY, Authorization: `Bearer ${SB_ANON_KEY}` },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          source: "contact_page",
          _hp: hp,
          elapsed_ms: Date.now() - openedAt.current,
          turnstile_token: await turnstile.getToken(),
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.error) {
        setStatus("error");
        setError(t("contact.form.errorServer", "Something went wrong. Please try again."));
        return;
      }

      setStatus("sent");
    } catch {
      setStatus("error");
      setError(t("contact.form.errorServer", "Something went wrong. Please try again."));
    }
  };

  return (
    <>
      <Helmet>
        <title>{tLang(locale, "contact.meta.title", "Contact Fyrris — Get in Touch")}</title>
        <meta name="description" content={tLang(locale, "contact.meta.description", "Questions about Fyrris? Reach out to our team. We help HPB specialty retailers get started with AR visualization.")} />
        <meta property="og:title" content={tLang(locale, "contact.meta.ogTitle", "Contact Fyrris — Get in Touch")} />
        <meta property="og:description" content={tLang(locale, "contact.meta.ogDescription", "Questions about Fyrris? Reach out to our team. We help HPB specialty retailers get started with AR visualization.")} />
        <meta property="og:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section
        className="pt-12 pb-16 md:pt-14 md:pb-20 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <p
            className="inline-block text-xs font-semibold tracking-widest uppercase mb-5 px-3 py-1 rounded-full"
            style={{ backgroundColor: `${EMBER}22`, color: EMBER }}
          >
            {t("contact.hero.eyebrow", "Get in Touch")}
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-5">
            {t("contact.hero.headline", "We'd love to hear from you.")}
          </h1>
          <p className="text-lg text-gray-300 leading-relaxed">
            {t(
              "contact.hero.subhead",
              "Questions about Fyrris, retailer onboarding, or anything else — send us a message and we'll get back to you within one business day."
            )}
          </p>
        </div>
      </section>

      {/* ── FORM ─────────────────────────────────────────────────────────────── */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-2xl mx-auto">
          {status === "sent" ? (
            <div className="text-center py-16">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: `${EMBER}22` }}
              >
                <Send className="w-7 h-7" style={{ color: EMBER }} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">
                {t("contact.form.successTitle", "Message sent!")}
              </h2>
              <p className="text-gray-300 leading-relaxed">
                {t(
                  "contact.form.successBody",
                  "Thanks for reaching out. We'll get back to you within one business day."
                )}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
              <input
              type="text"
              name="_hp"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={hp}
              onChange={(e) => setHp(e.target.value)}
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            />
            {turnstile.enabled && <div ref={turnstile.ref} />}
                          <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-300">
                  {t("contact.form.name", "Your name")}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("contact.form.namePlaceholder", "Jane Smith")}
                  className="rounded-xl border border-gray-700 bg-gray-900 text-white placeholder-gray-500 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  style={{ caretColor: EMBER }}
                  disabled={status === "sending"}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-300">
                  {t("contact.form.email", "Email address")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("contact.form.emailPlaceholder", "jane@yourstore.com")}
                  className="rounded-xl border border-gray-700 bg-gray-900 text-white placeholder-gray-500 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  style={{ caretColor: EMBER }}
                  disabled={status === "sending"}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-300">
                  {t("contact.form.message", "Message")}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t(
                    "contact.form.messagePlaceholder",
                    "Tell us about your store, your question, or how we can help…"
                  )}
                  rows={5}
                  className="rounded-xl border border-gray-700 bg-gray-900 text-white placeholder-gray-500 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                  style={{ caretColor: EMBER }}
                  disabled={status === "sending"}
                />
              </div>

              {error && (
                <p className="text-sm text-red-400">{error}</p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: EMBER, boxShadow: "0 4px 14px rgba(255,107,53,0.3)" }}
              >
                <Send className="w-4 h-4" />
                {status === "sending"
                  ? t("contact.form.sending", "Sending…")
                  : t("contact.form.submit", "Send Message")}
              </button>

              <p className="text-xs text-gray-400 text-center">
                {t("contact.form.alt", "Prefer email? Reach us at")}{" "}
                <a
                  href="mailto:support@fyrris.com"
                  className="underline hover:text-gray-300 transition"
                >
                  support@fyrris.com
                </a>
              </p>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
